import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../components/cancelButton/CancelButton";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import ExpandableTasks from "../tasks/ExpandableTasks";
import CallRecordings from "./callRecordings/CallRecordings";
import type { CustomerFormState, TagsState } from "./CustomerEdit";
import CustomerEdit from "./CustomerEdit";
import type { AdminCustomerResponse } from "./customersApi";
import LoyaltyPoints from "./loyalty-points/LoyaltyPoints";
import CustomerNotes from "./notes/CustomerNotes";
import CustomerOverview from "./overview/CustomerOverview";
import { useCustomer } from "./useCustomer";

function customerResponseToState(value: AdminCustomerResponse): CustomerFormState {
  const { publicContact, travelPay } = value;
  return {
    name: value.name,
    accountManager: value.accountManager?.id ?? null,
    status: value.status,
    contactDate: value.contactDate ? dayjs(value.contactDate, ISO_DATE_FORMAT) : null,
    meetingDate: value.meetingDate ? dayjs(value.meetingDate, ISO_DATE_FORMAT) : null,
    contacts: (value.contacts ?? []).map((c) => ({
      name: c.name ?? "",
      phone: c.phone ?? "",
      email: c.email ?? "",
      loginEmail: c.loginEmail ?? "",
      position: c.position ?? "",
      privileges: c.privileges,
    })),
    invoiceEmail: value.invoiceEmail ?? "",
    info: value.info ?? "",
    nip: value.taxNo ?? "",
    publicContactName: publicContact.name ?? "",
    publicContactAddress: publicContact.address ?? "",
    publicContactPhone: publicContact.phone ?? "",
    publicContactEmail: publicContact.email ?? "",
    publicContactWebpage: publicContact.webpage ?? "",
    publicContactGooglePlaceId: publicContact.googlePlaceId ?? "",
    publicContactCity: publicContact.city ?? "",
    publicContactvoivodeship: publicContact.voivodeship ?? "",
    travelPayStatus: travelPay.status,
    travelPayAccountManager: travelPay.accountManager?.id ?? null,
  };
}

interface ExistingCustomerEditProps {
  readonly value: AdminCustomerResponse;
  readonly onSubmit: (state: CustomerFormState, tags: TagsState) => Promise<void>;
  readonly onDelete: () => Promise<void>;
  readonly onCancel: () => void;
}

const ExistingCustomerEdit: FC<ExistingCustomerEditProps> = ({ value, onSubmit, onDelete, onCancel }) => {
  const { context } = useAuthContext();
  const [tags, setTags] = useState<TagsState>(value.tags);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const { control, handleSubmit } = useForm<CustomerFormState>({
    defaultValues: customerResponseToState(value),
  });

  const canEdit =
    context?.role === "ADMIN" ||
    (context?.role === "AGENT" &&
      (context?.agentId === value.accountManager?.id || !context?.customPrivileges.onlyOwnCustomersEdit));
  const canChangeAccountManager =
    context?.role === "ADMIN" ||
    (context?.role === "AGENT" && context?.customPrivileges.canChangeCustomerAccountManager);
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
        paddingBottom: 2,
      }}
    >
      <Grid container size={{ xs: 12, lg: 9 }} spacing={2}>
        <Grid container size={12}>
          <CustomerEdit
            control={control}
            tags={tags}
            setTags={setTags}
            readonly={!canEdit}
            accountManagerDisabled={!canEdit || !canChangeAccountManager}
            filesEnabled
            customer={value}
          />
        </Grid>
        <Grid
          size={12}
          container
          sx={{
            justifyContent: "flex-end",
            alignItems: "start",
          }}
        >
          <Stack direction="row" spacing={2}>
            <CancelButton onClick={onCancel} />
            <Button
              variant="outlined"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmationDialogVisible(true)}
            >
              Usuń
            </Button>
            <AsyncLoadingButton variant="outlined" disabled={!canEdit} onClick={handleSubmit((s) => onSubmit(s, tags))}>
              Zapisz
            </AsyncLoadingButton>
            <ConfirmationDialog
              open={confirmationDialogVisible}
              title="Usuń klienta"
              content="Czy chcesz usunąć klienta? Tej operacji nie będzie można już cofnąć."
              onCancel={() => setConfirmationDialogVisible(false)}
              onConfirm={onDelete}
            />
          </Stack>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, lg: 3 }} spacing={2}>
        <Grid container size={12}>
          <CustomerOverview customerId={value.id} />
        </Grid>
      </Grid>
      <Grid size={12} data-testid="przezmorze-notes">
        <CustomerNotes customerId={value.id} category="PRZEZMORZE" title="Notatki Przezmorze" readonly={!canEdit} />
      </Grid>
      <Grid size={12} data-testid="travelpay-notes">
        <CustomerNotes customerId={value.id} category="TRAVEL_PAY" title="Notatki TravelPay" readonly={!canEdit} />
      </Grid>
      <Grid size={12} data-testid="tasks">
        <ExpandableTasks entityType="customers" entityId={value.id} customerId={value.id} />
      </Grid>
      <Grid size={12} data-testid="loyalty-points">
        <LoyaltyPoints contacts={value.contacts ?? []} readonly={!canEdit} />
      </Grid>
      <Grid size={12} data-testid="call-recordings">
        <CallRecordings contacts={value.contacts ?? []} customerId={value.id} />
      </Grid>
    </Grid>
  );
};

const EditCustomerPage = () => {
  const navigate = useNavigate();
  const { customer: value, isLoading, updateCustomer, deleteCustomer } = useCustomer();

  const handleSubmit = async (state: CustomerFormState, tags: TagsState) => {
    await updateCustomer({
      name: state.name,
      contactDate: state.contactDate ? state.contactDate.format(ISO_DATE_FORMAT) : undefined,
      taxNo: state.nip || undefined,
      contacts: state.contacts.map((c) => ({
        name: c.name ? c.name : undefined,
        phone: c.phone ? c.phone : undefined,
        email: c.email ? c.email : undefined,
        loginEmail: c.loginEmail ? c.loginEmail : undefined,
        position: c.position ? c.position : undefined,
        privileges: c.privileges,
      })),
      invoiceEmail: state.invoiceEmail ? state.invoiceEmail : undefined,
      accountManagerId: state.accountManager ? state.accountManager : undefined,
      status: state.status,
      tags,
      info: state.info ? state.info : undefined,
      publicContact: {
        name: state.publicContactName ? state.publicContactName : undefined,
        address: state.publicContactAddress ? state.publicContactAddress : undefined,
        phone: state.publicContactPhone ? state.publicContactPhone : undefined,
        email: state.publicContactEmail ? state.publicContactEmail : undefined,
        webpage: state.publicContactWebpage ? state.publicContactWebpage : undefined,
        googlePlaceId: state.publicContactGooglePlaceId ? state.publicContactGooglePlaceId : undefined,
        city: state.publicContactCity ? state.publicContactCity : undefined,
        voivodeship: state.publicContactvoivodeship ? state.publicContactvoivodeship : undefined,
      },
      travelPay: {
        status: state.travelPayStatus,
        accountManagerId: state.travelPayAccountManager ?? undefined,
      },
    });
  };

  const handleDelete = async () => {
    await deleteCustomer();
    navigate("/customers");
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }
  if (!value) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Taki klient nie istnieje</Typography>
      </Grid>
    );
  }
  return (
    <ExistingCustomerEdit
      value={value}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      onCancel={() => navigate("/customers")}
    />
  );
};

export default EditCustomerPage;
